import { Link, useLoaderData } from 'react-router'
import { PayloadFooter, PayloadHeader } from '#app/routes/_marketing+/index.tsx'
import { MediaImage } from '#app/components/image.tsx'
import { getBlogPosts } from '#app/utils/blog.server.ts'
import {
	createWebPageSchema,
	organizationSchema,
	webSiteSchema,
} from '#app/utils/constants.ts'
import { type Route } from './+types/_index.ts'

const MetaTitle = 'Blog | DealerProEdge'
const MetaDescription =
	'Ideas and practical notes for dealership teams improving website speed, SEO, merchandising, and lead conversion.'

export const handle = { chrome: 'payload' }

export async function loader() {
	return { posts: await getBlogPosts() }
}

export const meta: Route.MetaFunction = ({ matches }) => {
	const rootData = matches[0]?.data as
		| { requestInfo?: { origin: string; path: string } }
		| undefined
	const origin = rootData?.requestInfo?.origin ?? 'https://dealerproedge.com'
	const path = rootData?.requestInfo?.path ?? '/blog'
	const canonicalUrl = `${origin}${path}`
	const pageSchema = createWebPageSchema({
		name: MetaTitle,
		description: MetaDescription,
		url: canonicalUrl,
	})

	return [
		{ title: MetaTitle },
		{ name: 'description', content: MetaDescription },
		{ name: 'twitter:title', content: MetaTitle },
		{ name: 'twitter:description', content: MetaDescription },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ property: 'og:title', content: MetaTitle },
		{ property: 'og:description', content: MetaDescription },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: canonicalUrl },
		{ tagName: 'link', rel: 'canonical', href: canonicalUrl },
		{ 'script:ld+json': organizationSchema },
		{ 'script:ld+json': webSiteSchema },
		{ 'script:ld+json': pageSchema },
	]
}

export default function BlogIndexRoute() {
	const { posts } = useLoaderData<typeof loader>()
	const [featuredPost, ...archivePosts] = posts

	return (
		<div className="dpe-live-page dpe-live-page--light dpe-blog-page">
			<PayloadHeader />
			<main>
				<section className="dpe-blog-archive" aria-labelledby="blog-title">
					{/*TODO: Put back*/}
				<div className="dpe-blog-archive-top">
						<p>Posts</p>
					<nav className="dpe-blog-tabs" aria-label="Post categories">
							<Link aria-current="page" to="/blog">
								Blog
							</Link>
							<Link to="/blog/guides">Guides</Link>
							<Link to="/blog/releases">Releases</Link>
							<Link to="/blog/case-studies">Case Studies ↗</Link>
						</nav>
					</div>

					<header className="dpe-blog-hero">
						<h1 id="blog-title">Keep tabs on Dealer Pro Edge</h1>
						<p>
							Here, you'll find practical notes about dealership website
							performance, search visibility, merchandising, lead conversion,
							and announcements in general.
						</p>
					</header>

					{featuredPost ? (
						<Link className="dpe-blog-featured" to={featuredPost.href}>
							<span className="dpe-blog-featured-media">
								<MediaImage
									alt=""
									aspectRatio={16 / 9}
									layout="fullWidth"
									src={featuredPost.image}
								/>
							</span>
							<span className="dpe-blog-featured-copy">
								<span className="dpe-blog-featured-title">
									{featuredPost.title}
								</span>
								<span className="dpe-blog-featured-meta">
									{featuredPost.date}
									<span>{featuredPost.author}</span>
								</span>
							</span>
						</Link>
					) : null}

					<div className="dpe-blog-list">
						{archivePosts.map((post) => (
							<Link className="dpe-blog-row" key={post.href} to={post.href}>
								<span className="dpe-blog-row-media">
									<MediaImage
										alt=""
										aspectRatio={16 / 9}
										layout="fullWidth"
										src={post.image}
									/>
								</span>
								<span className="dpe-blog-row-copy">
									<span className="dpe-blog-row-meta">
										<span>{post.date}</span>
										<span>{post.author}</span>
									</span>
									<span className="dpe-blog-row-title">{post.title}</span>
								</span>
							</Link>
						))}
					</div>
				</section>
			</main>
			<PayloadFooter />
		</div>
	)
}
